import { redirect } from 'next/navigation'
import { NextResponse } from 'next/server'
import { and, eq } from 'drizzle-orm'
import { auth } from '@/auth'
import { db } from '@/db'
import { users, tenantMemberships } from '@/db/schema'

export type ExpertUser = {
  id: string
  tenantId: string
  role: 'expert' | 'admin'
}

function isExpertRole(role: string): role is ExpertUser['role'] {
  return role === 'expert' || role === 'admin'
}

async function resolveMembership(userId: string, tenantId: string) {
  if (!tenantId) return null

  const [membership] = await db
    .select({
      id: users.id,
      tenantId: tenantMemberships.tenantId,
      role: tenantMemberships.role,
      isActive: tenantMemberships.isActive,
    })
    .from(users)
    .innerJoin(
      tenantMemberships,
      and(
        eq(tenantMemberships.userId, users.id),
        eq(tenantMemberships.tenantId, tenantId),
      )
    )
    .where(eq(users.id, userId))
    .limit(1)

  return membership
}

export async function requireExpertUser(): Promise<ExpertUser> {
  const session = await auth()
  if (!session?.user?.id) redirect('/login')

  const membership = await resolveMembership(session.user.id, session.user.tenantId ?? '')

  if (!membership?.tenantId || !isExpertRole(membership.role) || !membership.isActive) redirect('/submit')

  return {
    id: membership.id,
    tenantId: membership.tenantId,
    role: membership.role,
  }
}

export async function requireExpertApiUser(): Promise<
  { ok: true; user: ExpertUser } | { ok: false; response: NextResponse }
> {
  const session = await auth()
  if (!session?.user?.id) {
    return { ok: false, response: NextResponse.json({ error: 'Unauthorized' }, { status: 401 }) }
  }

  const membership = await resolveMembership(session.user.id, session.user.tenantId ?? '')

  if (!membership?.tenantId || !isExpertRole(membership.role) || !membership.isActive) {
    return { ok: false, response: NextResponse.json({ error: 'Forbidden' }, { status: 403 }) }
  }

  return {
    ok: true,
    user: {
      id: membership.id,
      tenantId: membership.tenantId,
      role: membership.role,
    },
  }
}

export async function requireAdminApiUser(): Promise<
  { ok: true; user: ExpertUser } | { ok: false; response: NextResponse }
> {
  const session = await auth()
  if (!session?.user?.id) {
    return { ok: false, response: NextResponse.json({ error: 'Unauthorized' }, { status: 401 }) }
  }

  const membership = await resolveMembership(session.user.id, session.user.tenantId ?? '')

  if (!membership?.tenantId || membership.role !== 'admin' || !membership.isActive) {
    return { ok: false, response: NextResponse.json({ error: 'Forbidden' }, { status: 403 }) }
  }

  return {
    ok: true,
    user: {
      id: membership.id,
      tenantId: membership.tenantId,
      role: membership.role,
    },
  }
}

export async function requireAdminUser(): Promise<ExpertUser> {
  const session = await auth()
  if (!session?.user?.id) redirect('/login')

  const membership = await resolveMembership(session.user.id, session.user.tenantId ?? '')

  if (!membership?.tenantId || membership.role !== 'admin' || !membership.isActive) redirect('/inbox')

  return {
    id: membership.id,
    tenantId: membership.tenantId,
    role: membership.role,
  }
}
